import { useEffect, useMemo, useState } from 'react';
import { Copy, KeyRound, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { createPatientPortalToken, loadPageData, loadPatientPortalAccessLog } from './data.js';
import { friendlyError } from './lib/format.js';

function fmtDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('id-ID', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function StatusBadge({ active }) {
  return (
    <span className={`pp-access-badge ${active ? 'active' : 'expired'}`}>
      {active ? 'Aktif' : 'Berakhir'}
    </span>
  );
}

function TokenCreator() {
  const [clientQuery, setClientQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [ttlHours, setTtlHours] = useState(720);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const selectedClient = useMemo(
    () => clients.find((row) => row.id === selectedClientId) || null,
    [clients, selectedClientId],
  );

  async function searchClients(event) {
    event?.preventDefault();
    setError('');
    setCreated(null);
    if (clientQuery.trim().length < 2) {
      setClients([]);
      setSelectedClientId('');
      setError('Isi minimal 2 huruf nama, nomor telepon, atau kode klien.');
      return;
    }
    setSearching(true);
    try {
      const rows = await loadPageData('clients', clientQuery);
      setClients(rows);
      setSelectedClientId(rows[0]?.id || '');
      if (!rows.length) setError('Pasien tidak ditemukan.');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSearching(false);
    }
  }

  async function createToken() {
    setError('');
    setCreated(null);
    if (!selectedClientId) {
      setError('Pilih pasien terlebih dahulu.');
      return;
    }
    setCreating(true);
    try {
      const result = await createPatientPortalToken(selectedClientId, Number(ttlHours) || 720);
      setCreated({ ...result, client: selectedClient });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setCreating(false);
    }
  }

  async function copyToken() {
    if (!created?.token) return;
    try {
      await navigator.clipboard.writeText(created.token);
    } catch {
      // Clipboard can fail outside secure browser contexts; token is still visible.
    }
  }

  return (
    <section className="pp-token-box">
      <div className="pp-token-head">
        <div>
          <span className="eyebrow">Token Portal</span>
          <h4>Buat Token Pasien</h4>
        </div>
        <KeyRound size={20} />
      </div>

      <form className="pp-token-search" onSubmit={searchClients}>
        <div>
          <label>Cari pasien</label>
          <input
            value={clientQuery}
            onChange={(event) => setClientQuery(event.target.value)}
            placeholder="Nama, telepon, atau KL-..."
          />
        </div>
        <button type="submit" disabled={searching}>
          <Search size={16} /> {searching ? 'Mencari...' : 'Cari'}
        </button>
      </form>

      {clients.length ? (
        <div className="pp-token-grid">
          <label>
            Pasien
            <select value={selectedClientId} onChange={(event) => setSelectedClientId(event.target.value)}>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} - {client.client_code} - {client.phone || 'tanpa telepon'}
                </option>
              ))}
            </select>
          </label>
          <label>
            Masa berlaku
            <select value={ttlHours} onChange={(event) => setTtlHours(Number(event.target.value))}>
              <option value={24}>24 jam</option>
              <option value={72}>3 hari</option>
              <option value={168}>7 hari</option>
              <option value={720}>30 hari</option>
            </select>
          </label>
          <button type="button" onClick={createToken} disabled={creating}>
            <KeyRound size={16} /> {creating ? 'Membuat...' : 'Buat Token'}
          </button>
        </div>
      ) : null}

      {error ? <div className="pp-access-error">{error}</div> : null}

      {created ? (
        <div className="pp-token-result">
          <div>
            <span>Token untuk {created.client?.name || 'pasien'}</span>
            <strong>{created.token}</strong>
            <small>Berakhir {fmtDateTime(created.expires_at)}</small>
          </div>
          <button type="button" onClick={copyToken}>
            <Copy size={15} /> Salin
          </button>
        </div>
      ) : null}
    </section>
  );
}

export function PatientPortalAccessPage() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      setRows(await loadPatientPortalAccessLog(150));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => [
      row.client_name,
      row.client_code,
      row.client_phone,
    ].some((value) => String(value || '').toLowerCase().includes(needle)));
  }, [query, rows]);

  return (
    <section className="pp-access-panel">
      <div className="pp-access-head">
        <div>
          <span className="eyebrow">Audit Portal Pasien</span>
          <h3>Pemantauan Akses Portal Pasien</h3>
          <p>Menampilkan session login pasien terbaru, termasuk waktu masuk dan aktivitas terakhir.</p>
        </div>
        <button type="button" onClick={load} disabled={loading}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      <TokenCreator />

      <div className="pp-access-tools">
        <Search size={17} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Cari nama, kode klien, atau telepon..."
        />
      </div>

      {error ? <div className="pp-access-error">{error}</div> : null}

      <div className="pp-access-summary">
        <ShieldCheck size={18} />
        <strong>{filtered.length}</strong>
        <span>akses ditampilkan</span>
      </div>

      <div className="pp-access-table-wrap">
        <table className="pp-access-table">
          <thead>
            <tr>
              <th>Pasien</th>
              <th>Kontak</th>
              <th>Masuk</th>
              <th>Terakhir Aktif</th>
              <th>Session</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="pp-access-empty">Memuat log akses...</td></tr>
            ) : filtered.length ? filtered.map((row) => (
              <tr key={row.session_id}>
                <td>
                  <strong>{row.client_name || '-'}</strong>
                  <span>{row.client_code || row.client_id}</span>
                </td>
                <td>{row.client_phone || '-'}</td>
                <td>{fmtDateTime(row.created_at)}</td>
                <td>{fmtDateTime(row.last_seen_at)}</td>
                <td>
                  <StatusBadge active={row.is_active} />
                  <small>Berakhir {fmtDateTime(row.expires_at)}</small>
                </td>
              </tr>
            )) : (
              <tr><td colSpan="5" className="pp-access-empty">Belum ada akses portal pasien.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .pp-access-panel { background: #fff; border: 1px solid #d7f3df; border-radius: 14px; padding: 24px; }
        .pp-access-head { align-items: flex-start; display: flex; gap: 16px; justify-content: space-between; }
        .pp-access-head h3 { color: #111827; font-size: 20px; margin: 4px 0; }
        .pp-access-head p { color: #6b7280; font-size: 13px; line-height: 1.5; margin: 0; }
        .pp-access-head button { align-items: center; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; color: #166534; cursor: pointer; display: inline-flex; font: inherit; font-size: 13px; font-weight: 800; gap: 7px; padding: 10px 14px; }
        .pp-access-head button:disabled { opacity: .7; cursor: wait; }
        .pp-token-box { background: #f7fff9; border: 1px solid #bbf7d0; border-radius: 14px; margin: 18px 0; padding: 18px; }
        .pp-token-head { align-items: flex-start; display: flex; justify-content: space-between; gap: 14px; }
        .pp-token-head h4 { color: #102f27; font-size: 18px; margin: 4px 0 0; }
        .pp-token-search { align-items: end; display: grid; gap: 12px; grid-template-columns: minmax(220px, 1fr) auto; margin-top: 14px; }
        .pp-token-search label, .pp-token-grid label { color: #102f27; display: grid; gap: 7px; font-size: 13px; font-weight: 800; }
        .pp-token-search input, .pp-token-grid select { background: #fff; border: 1px solid #baf0cc; border-radius: 12px; color: #111827; font: inherit; padding: 12px 13px; }
        .pp-token-search button, .pp-token-grid button, .pp-token-result button { align-items: center; background: #16a34a; border: 0; border-radius: 12px; color: white; cursor: pointer; display: inline-flex; font: inherit; font-weight: 900; gap: 7px; justify-content: center; padding: 12px 15px; }
        .pp-token-search button:disabled, .pp-token-grid button:disabled { cursor: wait; opacity: .7; }
        .pp-token-grid { align-items: end; display: grid; gap: 12px; grid-template-columns: minmax(260px, 1.7fr) minmax(160px, .8fr) auto; margin-top: 12px; }
        .pp-token-result { align-items: center; background: #fffdf8; border: 1px solid #f5d68a; border-radius: 12px; display: flex; gap: 14px; justify-content: space-between; margin-top: 14px; padding: 14px; }
        .pp-token-result span { color: #6b7280; display: block; font-size: 12px; font-weight: 800; }
        .pp-token-result strong { color: #102f27; display: block; font-family: ui-monospace, SFMono-Regular, Consolas, monospace; font-size: 24px; letter-spacing: .12em; margin: 4px 0; }
        .pp-token-result small { color: #6b7280; display: block; }
        .pp-access-tools { align-items: center; background: #fffdf8; border: 1px solid #d7f3df; border-radius: 12px; display: flex; gap: 9px; margin: 18px 0 12px; padding: 0 12px; max-width: 430px; }
        .pp-access-tools input { border: 0; background: transparent; flex: 1; font: inherit; outline: 0; padding: 12px 0; }
        .pp-access-error { background: #fee2e2; border: 1px solid #fecaca; border-radius: 10px; color: #991b1b; font-size: 13px; font-weight: 700; margin: 12px 0; padding: 10px 12px; }
        .pp-access-summary { align-items: center; color: #166534; display: flex; gap: 7px; font-size: 13px; margin: 10px 0 14px; }
        .pp-access-summary strong { font-size: 18px; }
        .pp-access-table-wrap { overflow-x: auto; }
        .pp-access-table { border-collapse: collapse; font-size: 13px; width: 100%; }
        .pp-access-table th { background: #f7f1e5; color: #6b6256; font-size: 11px; letter-spacing: .08em; padding: 12px; text-align: left; text-transform: uppercase; }
        .pp-access-table td { border-bottom: 1px solid #eef2e8; color: #374151; padding: 13px 12px; vertical-align: top; }
        .pp-access-table td strong { color: #102f27; display: block; }
        .pp-access-table td span, .pp-access-table td small { color: #6b7280; display: block; font-size: 12px; margin-top: 3px; }
        .pp-access-badge { border-radius: 999px; display: inline-flex !important; font-size: 12px !important; font-weight: 800; line-height: 1; margin: 0 0 5px; padding: 7px 10px; }
        .pp-access-badge.active { background: #dcfce7; color: #15803d; }
        .pp-access-badge.expired { background: #f3f4f6; color: #6b7280; }
        .pp-access-empty { color: #6b7280; font-weight: 700; padding: 28px !important; text-align: center; }
        @media (max-width: 760px) {
          .pp-access-head { flex-direction: column; }
          .pp-access-head button, .pp-access-tools { width: 100%; }
          .pp-token-search, .pp-token-grid { grid-template-columns: 1fr; }
          .pp-token-result { align-items: stretch; flex-direction: column; }
        }
      `}</style>
    </section>
  );
}
